import type { Notebook, SavedSearch, Tag } from "@engram/core";
import type { EntityName } from "@engram/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useQueryState, useRow } from "../db/client";
import { MultiPanel } from "./Bulk";
import { NoteList, type ClickMods } from "./NoteList";
import { NoteView } from "./NoteView";
import { useLayout } from "./layout";
import { usePrefs } from "./prefs";
import { Splitter } from "./Splitter";
import { TabStrip } from "./Tabs";
import { leftNote } from "./emptyNotes";

const NO_TERMS: string[] = [];
const goBack = () => history.back();
import { contextFromPath, filtersFromSearch, listQuery, writeFilters, type ListContext, type ListFilters, type NoteRow } from "./queries";

/** Cards drawn at first; scrolling near the end of a list adds five more pages. */
const PAGE = 40;
/** How many cards each list had drawn, so a list drawn again (phone back from a note) can scroll back to where it was. */
const drawnMemory = new Map<string, number>();

export function NotesPane() {
  const [path, navigate] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const selected = params.get("n");
  const savedId = params.get("saved");
  const prefs = usePrefs();
  const { listCollapsed: collapsedPref, view: viewPref } = prefs;
  const layout = useLayout();
  const single = layout === "narrow";
  const listCollapsed = !single && collapsedPref;
  // Top list needs the width; on a phone it falls back to the side list.
  const view = single && viewPref === "top" ? "side" : viewPref;

  const saved = useRow<SavedSearch>(savedId ? "SELECT * FROM saved_searches WHERE id = ? AND deleted = 0" : null, [savedId], ["saved_searches"]);
  let ctx = contextFromPath(path, search);
  if (ctx?.kind === "search" && savedId) ctx = saved ? { kind: "search", q: saved.query } : null;
  const filters = filtersFromSearch(search);
  const [sort, sortDesc] = ctx?.kind === "trash" ? [prefs.trashSort, prefs.trashSortDesc] : [prefs.sort === "deleted" ? "updated" : prefs.sort, prefs.sortDesc];

  // Memoized: `q.terms` goes to the editor (search highlights), which must not see a new array on every refresh.
  const qKey = JSON.stringify([ctx, sort, sortDesc, filters]);
  const q = useMemo(() => (ctx ? listQuery(ctx, sort, sortDesc, filters) : null), [qKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const deps: EntityName[] = ["notes", "attachments", "tasks", "notebooks", "tags", "shortcuts"];
  // Every matching id (count, select-all, keyboard, ranges), but full rows only for the cards drawn: at ~9,500
  // notes the full list cost ~150 ms in the database worker after every change, the first 240 rows 7 ms (chat 9).
  const idRows = useQueryState<{ id: string }>(q?.idsSql ?? null, q?.idsParams ?? [], deps);
  const ids = useMemo(() => idRows.rows?.map((r) => r.id), [idRows.rows]);
  const listKey = `${path}?${savedId ?? ""}${q?.idsSql ?? ""}${JSON.stringify(q?.idsParams ?? [])}`;
  const [want, setWant] = useState({ key: "", n: 0 });
  const wanted = want.key === listKey ? want.n : (drawnMemory.get(listKey) ?? PAGE);
  const selIndex = selected && ids ? ids.indexOf(selected) : -1;
  // Rounded up so moving the selection within a page doesn't change the query.
  const rowsWanted = Math.ceil(Math.max(wanted, selIndex + 1) / PAGE) * PAGE;
  // Searches fetch the drawn rows by id (their WHERE is a scan); other lists run in parallel with the ids query.
  const byIds = ctx?.kind === "search";
  const drawn = byIds && ids ? JSON.stringify(ids.slice(0, rowsWanted)) : null;
  const rowsState = useQueryState<NoteRow>(
    q ? (byIds ? (drawn !== null ? q.byIdsSql : null) : `${q.sql} LIMIT ${rowsWanted}`) : null,
    q ? (byIds ? (drawn !== null ? q.byIdsParams(drawn) : []) : q.params) : [],
    deps,
  );
  const notes = ids ? rowsState.rows : undefined;
  const fresh = idRows.fresh && rowsState.fresh;
  const more = useCallback(() => {
    drawnMemory.set(listKey, rowsWanted + PAGE * 5);
    setWant({ key: listKey, n: rowsWanted + PAGE * 5 });
  }, [listKey, rowsWanted]);
  const title = useContextTitle(ctx, saved?.name);

  // Multi-select: Ctrl/Shift-click on desktop, long-press on a phone. Cleared when the list changes.
  const [multi, setMulti] = useState<string[]>([]);
  const [selecting, setSelecting] = useState(false);
  const anchorId = useRef<string | null>(null);
  useEffect(() => {
    setMulti([]);
    setSelecting(false);
  }, [path, savedId]);
  // Drop ids that left the list (trashed, moved, filtered out).
  useEffect(() => {
    if (!ids || !fresh || !multi.length) return;
    const all = new Set(ids);
    if (multi.some((id) => !all.has(id))) setMulti((m) => m.filter((id) => all.has(id)));
  }, [ids, fresh, multi]);

  // Like Evernote, opening a list opens its first note.
  useEffect(() => {
    if (!ids || !fresh || listCollapsed || single) return;
    if (!selected && ids[0]) {
      const p = new URLSearchParams(search);
      p.set("n", ids[0]);
      const open = () => navigate(`${path}?${p}`, { replace: true });
      if (ctx?.kind !== "search") return open();
      // Search results paint first (a frame, then a task after it); the first hit, often a big note, opens right after.
      let t: ReturnType<typeof setTimeout> | undefined;
      const f = requestAnimationFrame(() => (t = setTimeout(open, 0)));
      return () => {
        cancelAnimationFrame(f);
        clearTimeout(t);
      };
    }
  }, [ids, fresh, selected, path, search, navigate, listCollapsed, single, ctx?.kind]);

  // Handlers read the latest state through a ref so they stay stable (note cards are memoized).
  const live = useRef({ ids, selected, multi, selecting, single, path, search });
  live.current = { ids, selected, multi, selecting, single, path, search };

  const select = useCallback(
    (id: string | null) => {
      const { path, search } = live.current;
      const p = new URLSearchParams(search);
      if (id) p.set("n", id);
      else p.delete("n");
      navigate(`${path}?${p}`);
    },
    [navigate],
  );
  // Leaving a note that is still empty removes it (emptyNotes.ts); the check waits for its pending saves.
  useEffect(() => {
    if (!selected) return;
    return () => leftNote(selected);
  }, [selected]);

  // Stable props, so the (memoized) note view doesn't re-render each time this list refreshes.
  const closeNote = useCallback(() => select(null), [select]);
  // After "Move to Trash" in the note (owner, 15 Oct, Fold cover: "take me… to the previous page where it shows me
  // all the notes", not the Trash): a phone goes back to where the note was opened from; desktop opens the next note
  // in the list (the previous one at the end), like Evernote and like Delete in the list.
  const afterTrash = useCallback(() => {
    const { ids, selected, single, path, search } = live.current;
    const p = new URLSearchParams(search);
    if (single) {
      if (history.length > 1) return history.back();
      p.delete("n");
      return navigate(`${path}?${p}`, { replace: true });
    }
    const at = selected && ids ? ids.indexOf(selected) : -1;
    const next = at >= 0 && ids ? (ids[at + 1] ?? ids[at - 1]) : undefined;
    if (next) p.set("n", next);
    else p.delete("n");
    navigate(`${path}?${p}`, { replace: true });
  }, [navigate]);

  const clearMulti = useCallback(() => {
    setMulti([]);
    setSelecting(false);
  }, []);

  const onPick = useCallback(
    (id: string, mods: ClickMods) => {
      const { ids, selected, multi, single } = live.current;
      if (mods.range && ids) {
        const from = anchorId.current ?? selected ?? id;
        const a = ids.indexOf(from);
        const b = ids.indexOf(id);
        if (a >= 0 && b >= 0) {
          setMulti(ids.slice(Math.min(a, b), Math.max(a, b) + 1));
          if (single) setSelecting(true);
          return;
        }
      }
      if (mods.toggle) {
        const base = multi.length || single ? multi : selected ? [selected] : [];
        const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
        anchorId.current = id;
        if (single) {
          setMulti(next);
          if (!next.length) setSelecting(false);
        } else if (next.length <= 1) {
          setMulti([]);
          if (next[0]) select(next[0]);
        } else setMulti(next);
        return;
      }
      anchorId.current = id;
      setMulti([]);
      select(id);
    },
    [select],
  );

  const onLongPress = useCallback((id: string) => {
    if (!live.current.single) return;
    setSelecting(true);
    setMulti((m) => (m.includes(id) ? m : [...m, id]));
  }, []);

  const selectAll = useCallback(() => {
    const { ids, single } = live.current;
    setMulti(ids ?? []);
    if (single) setSelecting(true);
  }, []);

  const setFilters = useCallback(
    (f: ListFilters) => {
      const { path, search } = live.current;
      const p = new URLSearchParams(search);
      writeFilters(p, f);
      p.delete("n"); // the open note may not match; the list opens its first note again
      navigate(`${path}?${p}`, { replace: true });
    },
    [navigate],
  );

  if (!ctx) return null;
  const showMulti = !single && multi.length > 1;

  return (
    <div className={`notes-pane${listCollapsed ? " list-collapsed" : ""}${single ? " single" : ""}${view === "top" && !listCollapsed ? " top-list" : ""}`}>
      {!listCollapsed && !(single && selected && !selecting) && (
        <NoteList
          ctx={ctx}
          title={title}
          hitKey={q?.terms.length ? JSON.stringify(q.terms) : ""}
          notes={notes}
          ids={ids}
          loading={!idRows.fresh}
          onMore={more}
          selected={selected}
          multi={multi}
          selecting={single && selecting}
          onPick={onPick}
          onLongPress={onLongPress}
          onClearMulti={clearMulti}
          onSelectAll={selectAll}
          filters={filters}
          onFilters={setFilters}
          savedId={savedId}
          view={view}
        />
      )}
      {/* Evernote: drag the list's edge to resize it (its bottom edge in Top list). */}
      {!listCollapsed && !single &&
        (view === "top" ? (
          <Splitter axis="y" cssVar="--list-h" min={140} max={(box) => box.height - 200} onCommit={(px) => usePrefs.getState().set({ listHeight: px })} />
        ) : (
          <Splitter cssVar="--list-w" min={240} max={(box) => Math.min(720, box.width - 360)} onCommit={(px) => usePrefs.getState().set({ listWidth: px })} />
        ))}
      {showMulti ? (
        <MultiPanel ids={multi} trash={ctx.kind === "trash"} onClear={clearMulti} />
      ) : selected && !(single && selecting) ? (
        layout === "wide" ? (
          // Desktop: note tabs above the editor (hidden until a second tab is opened).
          <div className="editor-col">
            <TabStrip selected={selected} onSelect={select} />
            <NoteView key={selected} id={selected} terms={q?.terms ?? NO_TERMS} onGone={closeNote} onTrashed={afterTrash} />
          </div>
        ) : (
          <NoteView key={selected} id={selected} terms={q?.terms ?? NO_TERMS} onGone={closeNote} onBack={single ? goBack : undefined} onTrashed={afterTrash} />
        )
      ) : (
        !single && <div className="editor-empty" />
      )}
    </div>
  );
}

function useContextTitle(ctx: ListContext | null, savedName?: string): string {
  const id = ctx && "id" in ctx ? ctx.id : null;
  const nb = useRow<Notebook>(ctx?.kind === "notebook" || ctx?.kind === "stack" ? "SELECT * FROM notebooks WHERE id = ?" : null, [id], ["notebooks"]);
  const tag = useRow<Tag>(ctx?.kind === "tag" ? "SELECT * FROM tags WHERE id = ?" : null, [id], ["tags"]);
  switch (ctx?.kind) {
    case "all":
      return "Notes";
    case "notebook":
    case "stack":
      return nb?.name ?? "";
    case "tag":
      return tag ? `Tag: ${tag.name}` : "";
    case "search":
      return savedName ?? (document.documentElement.classList.contains("layout-narrow") ? "Search" : "Search results");
    case "trash":
      return "Trash";
    case "shortcuts":
      return "Shortcuts";
    default:
      return "";
  }
}
